/**
 * Модерация публикаций: типы ответов `/api/posts*` и чистые правила для страниц.
 *
 * Правила (хэштеги, полный текст, «почему нельзя утвердить») повторяют `reels/postplan.py`:
 * сервер — истина, фронт считает то же, чтобы кнопка гасла сразу, без запроса.
 * Утверждение — НА СЕТЬ (№285): причины отказа приходят структурой «общее + по сетям», и гейт
 * считается для каждой сети отдельно (`netApproveProblems`). Лимит элементов карусели в гейт
 * больше не входит — он стал предупреждением, и строку для него (`PostNetwork.media_note`) даёт
 * сервер: считать её второй раз здесь нельзя, два расчёта однажды разойдутся.
 * Модуль грузит `node --test`, поэтому импорт — с расширением.
 */
import { cropRect, type Aspect, type Rect } from "./post.ts";

export type PostNetworkCode = "instagram" | "pinterest" | "vk";

export const NETWORKS: PostNetworkCode[] = ["instagram", "pinterest", "vk"];

export const NETWORK_LABEL: Record<PostNetworkCode, string> = {
  instagram: "Instagram",
  pinterest: "Pinterest",
  vk: "VK",
};

/** Лимиты формата сети (`post_format`, ключи без префикса). `null` — поля в сети нет. */
export interface PostFormat {
  aspect: string;
  width: number;
  height: number;
  title_max: number | null;
  title_fold: number | null;
  text_max: number;
  text_fold: number | null;
  hashtag_max: number;
  alt_min: number | null;
  alt_max: number | null;
  /** Сколько элементов карусели берёт сеть (`post_format_media_max`); Pinterest — 1. */
  media_max: number;
}

/** Рамка в долях оригинала. */
export interface Crop {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Сеть публикации (`postplan._networks`). Рамка сюда больше не входит — она переехала на элемент
 * карусели (`PostMedia.crop`, §3.1 спеки «Публикация из нескольких элементов и видео в ней»). */
export interface PostNetwork {
  title: string | null;
  text: string | null;
  hashtags: string | null;
  alt: string | null;
  gen_id: number | null;
  edited: boolean;
  crm_location_id: number | null;
  city_name: string | null;
  active: boolean;
  /** Свой статус сети (№285): утверждают, возвращают в черновик и отменяют КАЖДУЮ отдельно. */
  status: PostStatus;
  /** Кто и когда утвердил ЭТУ сеть; у черновика — `null`. */
  approver_name: string | null;
  date_approve: string | null;
  /** Дата выкладки (план, №383): ISO без часового пояса, `null` — не назначена. */
  publish_date: string | null;
  /** Сколько элементов карусели сеть реально возьмёт (`postplan.network_media`). */
  media_take: number;
  /** Что сеть НЕ возьмёт — готовая строка сервера («возьмёт только обложку, в публикации 2»);
   * `null` — всё помещается. Это предупреждение, а не запрет утверждения (№285). */
  media_note: string | null;
  /** Дубль кадра (№242, только Instagram) — готовая строка сервера: «одинаковый пост в N аккаунтах
   * Instagram — …» и/или «этот кадр уже идёт в @acc (публикация #M)»; `null` — дублей нет. Это
   * предупреждение, а не запрет утверждения. */
  dup_note: string | null;
  /** Та же строка кусками (`postplan.dup_parts`): кусок с `post_id` рисуется ссылкой на `/posts/M`;
   * склейка `text` — ровно `dup_note`. Пусто — дублей нет. */
  dup_parts: DupPart[];
}

/** Кусок строки `dup_note`: текст и, у «публикация #M», номер публикации для ссылки. */
export interface DupPart {
  text: string;
  post_id: number | null;
}

/** Показывать ли строку дубля в карточке сети: есть строка и сеть не отменена (как `media_note`). */
export function dupShown(net: PostNetwork): DupPart[] | null {
  return net.dup_note && net.status !== "cancelled" && net.dup_parts.length > 0 ? net.dup_parts : null;
}

/** Счётчики утверждения публикации (`postplan._approval`): живые активные сети, утверждённые из
 * них и признак «утверждено частично» — такая публикация остаётся ЧЕРНОВИКОМ. */
export interface PostApproval {
  active: PostNetworkCode[];
  approved: PostNetworkCode[];
  partial: boolean;
}

/** Почему нельзя утвердить — структурой, как `postplan.problems`: общее публикации (нет серии CRM,
 * не выбран ни один аккаунт) плюс причины каждой живой активной сети БЕЗ подписи сети — её даёт
 * ключ. Сети нет в `networks` — она неактивна (ни одного отмеченного аккаунта) или отменена. */
export interface PostProblems {
  common: string[];
  networks: Partial<Record<PostNetworkCode, string[]>>;
}

export interface PostGen {
  id: number;
  call: string | null;
  title: string | null;
  text: string | null;
  hashtags: string | null;
  alt: string | null;
  warnings: string[];
  base_number: number;
  network_number: number;
  date_create: string | null;
}

export interface PostAccount {
  id: number;
  handle: string;
  name: string | null;
  crm_location_id: number | null;
  city_name: string | null;
  archived: boolean;
  checked: boolean;
  also_in: number[];
}

export interface PostAccountNetwork {
  code: PostNetworkCode;
  name: string;
  accounts: PostAccount[];
}

export interface PostCity {
  crm_location_id: number;
  name: string;
  serials?: number;
}

export interface PostDress {
  id: number | null;
  /** Артикул CRM (побеждает `bound_series`) — он идёт в «Арт.» и в публикацию. */
  series: string | null;
  /** Чем платье привязали (серия экземпляра или артикул, ключ привязки) — для подписи «привязано по серии N». */
  bound_series: string | null;
  model_name: string | null;
  price_from: number | null;
  /** `null` — ответ без CRM (`dress_partial`). */
  cities: PostCity[] | null;
  crm_error: string | null;
}

export type PostStatus = "draft" | "approved" | "cancelled";

/** Элемент карусели (`post_plan_media`, `postplan._media`): фото или видео по порядку. Видео — без
 * рамки (`crop` у него не хранится), с постером (`variant_frame` 0 мс) и длительностью. */
export interface PostMedia {
  id: number;
  variant_id: number;
  asset_path: string;
  width: number | null;
  height: number | null;
  video_ms: number | null;
  poster_path: string | null;
  /** Рамки по сетям, доли оригинала; `null` — рамки нет вовсе (у видео — всегда `null`). */
  crop: Record<string, Crop | null> | null;
  sort: number;
}

export interface PostDetail {
  id: number;
  status: PostStatus;
  variant: { id: number; asset_path: string; width: number | null; height: number | null };
  formats: Record<string, PostFormat>;
  networks: Record<string, PostNetwork>;
  gens: Record<string, PostGen[]>;
  /** Элементы карусели по порядку; `media[0]` (`sort = 0`) — обложка, та же, что `variant`. */
  media: PostMedia[];
  accounts: PostAccountNetwork[];
  prompts: Record<string, number>;
  dress: PostDress;
  /** Салоны для метки VK; в ответах без CRM нет. */
  salons?: PostCity[] | undefined;
  dress_partial: boolean;
  estimate: number;
  author_name: string | null;
  approver_name: string | null;
  date_approve: string | null;
  date_modify: string;
  /** Счётчики «утверждено частично» (№285). */
  approval: PostApproval;
  problems: PostProblems;
}

/** Строка списка `GET /api/posts?status=`. */
export interface PostListItem {
  id: number;
  variant_id: number;
  asset_path: string;
  /** Обложка — видео: постер (может отсутствовать), не сам ролик (`postplan.listing`). */
  video_ms: number | null;
  poster_path: string | null;
  series: string | null;
  networks: PostNetworkCode[];
  /** Уже утверждённые сети (№285) — плитка пишет «утверждено частично», сама публикация при этом
   * остаётся черновиком. */
  approved_networks: PostNetworkCode[];
  accounts: number;
  author_name: string | null;
  date_modify: string | null;
  date_approve: string | null;
  /** Элементов в карусели (`postplan.listing`); «+N» на плитке `/posts` — `media_count - 1`. */
  media_count: number;
}

/** Кандидат для новой публикации/элемента карусели — `GET /api/posts/candidates`.
 * `kind=photo` (умолчание) — без видео-полей; `kind=video` несёт `video_ms`/`poster_path`
 * (постер может отсутствовать) и не требует метки «В публикацию». */
export interface PostCandidate {
  variant_id: number;
  asset_path: string;
  dress_id: number | null;
  series: string | null;
  plans: { id: number; status: PostStatus }[];
  video_ms?: number | null;
  poster_path?: string | null;
}

/** Версия шаблона текста (`postprompt`); в истории `text` нет. */
export interface PromptVersion {
  id: number;
  number: number;
  /** область версии; есть в ответе одной версии */
  scope?: string;
  text?: string;
  author_name: string | null;
  date_create: string | null;
  note: string | null;
  gens?: number;
}

// --- хэштеги и текст -----------------------------------------------------------------

const SPLIT = /[\s,;]+/;

/** Через пробел, каждый с `#`; дубли без учёта регистра — по первому написанию. */
export function normalizeHashtags(raw: string): string {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const tok of (raw ?? "").split(SPLIT)) {
    const word = tok.replace(/^#+/, "");
    const key = word.toLowerCase();
    if (word && !seen.has(key)) {
      seen.add(key);
      out.push("#" + word);
    }
  }
  return out.join(" ");
}

/** Сколько хэштегов (слов через пробел), как `len(tags.split())`. */
export function hashtagCount(tags: string): number {
  return (tags ?? "").split(/\s+/).filter(Boolean).length;
}

/** Текст так, как его увидит сеть: хэштеги в конце через пустую строку. */
export function fullText(text: string, hashtags: string): string {
  const t = (text ?? "").trimEnd();
  const h = (hashtags ?? "").trim();
  return h ? `${t}\n\n${h}` : t;
}

/** Длина в символах (как `len` в Python), не в UTF-16-единицах. */
const len = (s: string): number => [...s].length;

/**
 * Что видно до «ещё…» (`fold` символов). `fold` 0 — всё. Режет по последнему пробелу до `fold`,
 * если он ближе 20 символов к краю, иначе ровно по `fold`.
 */
export function foldSplit(text: string, fold: number): { shown: string; rest: string } {
  const chars = [...(text ?? "")];
  if (!fold || chars.length <= fold) return { shown: text ?? "", rest: "" };
  const head = chars.slice(0, fold).join("");
  const space = head.lastIndexOf(" ");
  const cut = space >= 0 && head.length - space <= 20 ? space : head.length;
  const whole = chars.join("");
  return { shown: whole.slice(0, cut), rest: whole.slice(cut).replace(/^ /, "") };
}

// --- «почему нельзя утвердить» ---------------------------------------------------------

function netProblems(net: PostNetwork, f: PostFormat): string[] {
  const title = net.title ?? "";
  const text = net.text ?? "";
  const tags = net.hashtags ?? "";
  const alt = net.alt ?? "";
  const out: string[] = [];
  if (f.title_max !== null) {
    if (!title.trim()) out.push("нет заголовка");
    else if (len(title) > f.title_max) out.push(`заголовок длиннее ${f.title_max}`);
  }
  if (!text.trim()) out.push("нет текста");
  else if (len(fullText(text, tags)) > f.text_max) out.push(`текст длиннее ${f.text_max}`);
  const count = hashtagCount(tags);
  if (count > f.hashtag_max) out.push(`хэштегов больше ${f.hashtag_max}`);
  if (f.alt_max !== null) {
    if (!alt.trim()) out.push("нет alt");
    else if (f.alt_min && len(alt) < f.alt_min) out.push(`alt короче ${f.alt_min}`);
    else if (len(alt) > f.alt_max) out.push(`alt длиннее ${f.alt_max}`);
  }
  return out;
}

/**
 * Почему нельзя утвердить — СТРУКТУРОЙ, как `postplan.problems` (№285): общее публикации плюс
 * причины каждой живой активной сети. Лимита элементов карусели здесь нет вовсе: сеть берёт первые
 * `media_take` и честно говорит об этом (`media_note`), а утверждение соседей не держит.
 */
export function approveProblems(d: PostDetail): PostProblems {
  const common: string[] = [];
  if (!d.dress?.series) common.push("нет серии CRM");
  if (!d.accounts.some((n) => n.accounts.some((a) => a.checked && !a.archived))) {
    common.push("не выбран ни один аккаунт");
  }
  const networks: Partial<Record<PostNetworkCode, string[]>> = {};
  for (const code of NETWORKS) {
    const net = d.networks[code];
    const fmt = d.formats[code];
    if (net && net.active && net.status !== "cancelled" && fmt) networks[code] = netProblems(net, fmt);
  }
  return { common, networks };
}

/** Что держит утверждение ОДНОЙ сети: общее публикации плюс её причины. Подписи сети в строках нет —
 * они показываются в её же карточке. Сети нет в структуре — её аккаунт не отмечен. */
export function netApproveProblems(found: PostProblems, code: PostNetworkCode): string[] {
  return [...found.common, ...(found.networks[code] ?? ["не выбран аккаунт"])];
}

/** Сети, готовые к утверждению — зеркало `postplan.approvable`. */
export function approvableNets(found: PostProblems): PostNetworkCode[] {
  if (found.common.length > 0) return [];
  return NETWORKS.filter((code) => found.networks[code]?.length === 0);
}

/** Сеть заморожена: утверждённую и отменённую не правят (поля, галочки аккаунтов, рамка, генерация
 * текста) — сервер отвечает `NOT_DRAFT` с её именем (№285).
 *
 * Зеркало серверного `postplan._frozen_nets`: утверждение морозит только АКТИВНУЮ сеть (есть
 * отмеченные аккаунты). Сеть без них экран не показывает — у неё нет ни карточки, ни кнопки
 * «вернуть в черновик», и, морозь её экран, галочки нельзя было бы вернуть (находка ревью 15.10). */
export function netFrozen(net: PostNetwork | undefined): boolean {
  if (!net) return false;
  return net.status === "cancelled" || (net.status === "approved" && net.active);
}

/** Имена сетей через запятую — для фраз «утверждено (VK)». */
export function networkNames(codes: readonly PostNetworkCode[]): string {
  return codes.map((code) => NETWORK_LABEL[code] ?? code).join(", ");
}

/** «утверждено частично: VK · ещё 2 в черновике» — когда утверждена ЧАСТЬ живых сетей; ничего не
 * утверждено или утверждено всё — `null` (об этом уже говорит статус публикации). */
export function partialNote(approved: readonly PostNetworkCode[], active: readonly PostNetworkCode[]): string | null {
  const rest = active.filter((code) => !approved.includes(code)).length;
  if (approved.length === 0 || rest === 0) return null;
  return `утверждено частично: ${networkNames(approved)} · ещё ${rest} в черновике`;
}

/** Подпись ЕДИНСТВЕННОЙ кнопки генерации текста — в карточке сети (№379: верхняя «Генерировать ‹Сеть›»
 * звала то же самое). Оценка — `plan.estimate`, один платный вызов на сеть. */
export function genLabel(generating: boolean, hasGens: boolean, estimate: number): string {
  if (generating) return "Генерирую…";
  const verb = hasGens ? "Перегенерировать" : "Сгенерировать";
  return estimate > 0 ? `${verb} ≈ $${estimate.toFixed(3)}` : verb;
}

/** Кнопка удаления в шапке открытой публикации (№378: с плитки убрана). Это отмена всей публикации
 * (`cancelled`); у отменённой кнопки нет. Частично утверждённая — всё ещё черновик, подтверждение
 * называет утверждённые сети. */
export function deletePlan(
  id: number,
  status: PostStatus,
  approved: readonly PostNetworkCode[],
): { label: string; confirm: string } | null {
  if (status === "cancelled") return null;
  if (status === "approved") {
    return { label: "Отменить публикацию", confirm: `Отменить публикацию №${id}? Все сети утверждены — её не будет нигде.` };
  }
  const also = approved.length > 0 ? ` В ней уже утверждено: ${networkNames(approved)}.` : "";
  return { label: "Удалить черновик", confirm: `Удалить черновик №${id}? Он пропадёт из списка «Черновики».${also}` };
}

// --- рамка ---------------------------------------------------------------------------

/** Рамка в пикселях → доли оригинала. */
export function cropFromRect(rect: Rect, w: number, h: number): Crop {
  return { x: rect.x / w, y: rect.y / h, w: rect.w / w, h: rect.h / h };
}

/** Доли → пиксели; `crop` null — наибольшая рамка пропорции по центру. */
export function rectFromCrop(crop: Crop | null, w: number, h: number, aspect: Aspect): Rect {
  if (!crop) return cropRect(w, h, aspect, 0.5, 0.5);
  return {
    x: Math.round(crop.x * w),
    y: Math.round(crop.y * h),
    w: Math.round(crop.w * w),
    h: Math.round(crop.h * h),
  };
}

/** Наибольшая рамка пропорции к рамке `crop` — во сколько раз кадр приближен (1 — не приближен). */
export function zoomOf(crop: Crop | null, w: number, h: number, aspect: Aspect): number {
  const cur = rectFromCrop(crop, w, h, aspect);
  return cur.w > 0 ? cropRect(w, h, aspect, 0.5, 0.5).w / cur.w : 1;
}

/**
 * Рамка той же пропорции, приближенная в `zoom` раз (1 — наибольшая), центр — центр текущей рамки, с упором
 * в края кадра (№447: «приблизить кадр, если много лишнего фона»). Пиксели оригинала.
 */
export function zoomRect(crop: Crop | null, w: number, h: number, aspect: Aspect, zoom: number): Rect {
  const cur = rectFromCrop(crop, w, h, aspect);
  const big = cropRect(w, h, aspect, 0.5, 0.5);
  const z = Math.max(1, zoom);
  const rw = Math.round(big.w / z);
  const rh = Math.round(big.h / z);
  const clamp = (v: number, hi: number) => Math.min(Math.max(Math.round(v), 0), hi);
  return {
    x: clamp(cur.x + cur.w / 2 - rw / 2, w - rw),
    y: clamp(cur.y + cur.h / 2 - rh / 2, h - rh),
    w: rw,
    h: rh,
  };
}

// --- аккаунты и слияние ----------------------------------------------------------------

/** Живые аккаунты с городом из `cities` (федеральные — без города — не входят). */
export function dressCityAccounts(accounts: PostAccount[], cities: number[]): number[] {
  const set = new Set(cities);
  return accounts
    .filter((a) => !a.archived && a.crm_location_id !== null && set.has(a.crm_location_id))
    .map((a) => a.id);
}

/** Ответ без CRM (`dress_partial`) не затирает платье и салоны, уже загруженные с CRM. */
export function mergeDetail(prev: PostDetail, next: PostDetail): PostDetail {
  if (!next.dress_partial) return next;
  return { ...next, dress: prev.dress, salons: prev.salons, dress_partial: prev.dress_partial };
}
